// Consortium work list grouped by the organisation that owes each item (US-08), from the latest as_is evaluations.
// The owner text of the rule pack is free text ("THODO / Innoplant"); it is matched to the organisations by their
// aliases, as public.owns_field() does, so an item owed by two partners appears in both lists.
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import { PageHeader } from '../components/Layout.tsx';
import { Enum, ErrorBox, Loading, Msg } from '../components/ui.tsx';
import { activePack, latestEvaluations, notices, pendingRequests, whoAmI } from '../lib/data.ts';
import { operatorClient } from '../lib/supabase.ts';
import { fieldDef, ownedBy, type FieldDef, type Org } from '../lib/template.ts';
import type { Reason } from '../lib/types.ts';

interface Item {
  batch: string;
  r: Reason;
}

// open points a partner closes by entering or correcting a value
const DATA_CODES = [
  'missing_mandatory',
  'pending_evidence',
  'not_evaluable_blocking',
  'genealogy_incomplete',
];
const ALL = 'all';
const OTHER = 'other';

export function Worklist() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState<Item[] | null>(null);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [fields, setFields] = useState<FieldDef[]>([]);
  const [criteria, setCriteria] = useState<Map<string, string>>(new Map());
  const [myOrg, setMyOrg] = useState<string | null>(null);
  const [queue, setQueue] = useState<
    { request_id: number; batch_id: string; reason: string; requested_at: string }[]
  >([]);
  const [notes, setNotes] = useState<
    { notification_id: number; kind: string; payload: Record<string, unknown>; created_at: string }[]
  >([]);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [evals, pack, who, o] = await Promise.all([
          latestEvaluations(),
          activePack(),
          whoAmI(),
          operatorClient().from('organisations').select('org_code, name, aliases').order('name'),
        ]);
        const list: Item[] = [];
        for (const e of evals.values()) {
          if (e.mode !== 'as_is') continue;
          for (const r of e.verdict.reasons) if (r.code !== 'warning') list.push({ batch: e.batch_id, r });
        }
        list.sort((a, b) => a.batch.localeCompare(b.batch));
        const p = pack?.pack as unknown as {
          fields: { items: FieldDef[] };
          rules: { items: { rule_id: string; criterion: string }[] };
        };
        setFields(p?.fields.items ?? []);
        setCriteria(new Map((p?.rules.items ?? []).map((x) => [x.rule_id, x.criterion])));
        setOrgs((o.data ?? []) as Org[]);
        setMyOrg(who?.org ?? null);
        setItems(list);
        setQueue(await pendingRequests());
        setNotes(await notices());
      } catch (e) {
        setError(e);
      }
    })();
  }, []);

  // the signed-in partner sees its own list first; anyone can switch to another organisation or to all
  const selected = params.get('org') ?? myOrg ?? ALL;
  const choose = (v: string) => {
    const next = new URLSearchParams(params);
    next.set('org', v);
    setParams(next, { replace: true });
  };

  const groups = useMemo(() => {
    const out = new Map<string, Item[]>();
    for (const it of items ?? []) {
      const owners = orgs.filter((o) => it.r.owner && ownedBy(it.r.owner, o)).map((o) => o.org_code);
      for (const code of owners.length > 0 ? owners : [OTHER]) out.set(code, [...(out.get(code) ?? []), it]);
    }
    const order = [...orgs.map((o) => o.org_code), OTHER];
    return order
      .filter((code) => out.has(code) && (selected === ALL || selected === code))
      .map((code) => [code, out.get(code) ?? []] as const);
  }, [items, orgs, selected]);

  if (!items) return <Loading />;
  const orgName = (code: string) =>
    code === OTHER ? t('worklist.other') : (orgs.find((o) => o.org_code === code)?.name ?? code);
  const name = (r: Reason) => {
    if (r.rule_id) return criteria.get(r.rule_id) ?? r.rule_id;
    return (r.field_id && fieldDef(fields, r.field_id)?.field_name) || r.field_id || '';
  };

  return (
    <main>
      <PageHeader title={t('worklist.title')} page="worklist" />
      <ErrorBox error={error} />
      <p className="filters">
        <label>
          {t('worklist.show')}{' '}
          <select value={selected} onChange={(e) => choose(e.target.value)}>
            <option value={ALL}>{t('worklist.all')}</option>
            {orgs.map((o) => (
              <option key={o.org_code} value={o.org_code}>
                {o.name}
                {o.org_code === myOrg ? ` (${t('worklist.mine')})` : ''}
              </option>
            ))}
            <option value={OTHER}>{t('worklist.other')}</option>
          </select>
        </label>{' '}
        <span className="muted small">{t('worklist.note')}</span>
      </p>
      {queue.length > 0 && (
        <section className="card">
          <h2>{t('worklist.queue', { count: queue.length })}</h2>
          <ul>
            {queue.map((q) => (
              <li key={q.request_id}>
                <Link to={`/batches/${q.batch_id}`}>{q.batch_id}</Link> · {q.reason}{' '}
                <span className="small muted">{q.requested_at.slice(0, 16).replace('T', ' ')}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {notes.length > 0 && (
        <details className="card">
          <summary>{t('worklist.notices', { count: notes.length })}</summary>
          <ul className="small">
            {notes.map((n) => (
              <li key={n.notification_id}>
                {n.created_at.slice(0, 10)} · <Enum ns="notice" v={n.kind} /> ·{' '}
                {String(n.payload['evidence_id'] ?? '')} {t('col.version')}{' '}
                {String(n.payload['version'] ?? '')} · {String(n.payload['reason'] ?? '')}
              </li>
            ))}
          </ul>
        </details>
      )}
      {groups.length === 0 && <p className="hint">{t('worklist.empty')}</p>}
      {groups.map(([code, list]) => (
        <section key={code}>
          <h2>
            {orgName(code)} ({list.length})
          </h2>
          {code === OTHER && <p className="muted small">{t('worklist.other_help')}</p>}
          <table>
            <thead>
              <tr>
                <th>{t('col.batch')}</th>
                <th>{t('worklist.col.what')}</th>
                <th>{t('worklist.col.item')}</th>
                <th>{t('worklist.col.needed')}</th>
                <th>{t('worklist.col.next')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map(({ batch, r }, i) => (
                <tr key={`${batch}-${r.rule_id ?? r.field_id ?? ''}-${i}`}>
                  <td className="nowrap">
                    <Link to={`/batches/${batch}`}>{batch}</Link>
                  </td>
                  <td>
                    <Msg k={`reason.${r.code}`} />
                  </td>
                  <td>
                    {name(r)}{' '}
                    {name(r) !== (r.rule_id ?? r.field_id) && (
                      <span className="muted small">
                        <code>{r.rule_id ?? r.field_id}</code>
                      </span>
                    )}
                    <br />
                    <span className="small">
                      <Msg k={r.message.key} />
                    </span>
                    {r.owner && (
                      <span className="muted small">
                        {' '}
                        · {t('worklist.owner_text')}: {r.owner}
                      </span>
                    )}
                  </td>
                  <td className="small">{r.evidence_required || <span className="muted">—</span>}</td>
                  <td className="nowrap small">
                    {DATA_CODES.includes(r.code) && code !== OTHER && (
                      <>
                        <Link
                          to={`/ingest?org=${encodeURIComponent(code)}&batch=${encodeURIComponent(batch)}`}
                        >
                          {t('worklist.go_ingest')}
                        </Link>
                        <br />
                      </>
                    )}
                    {r.evidence_required && (
                      <>
                        <Link to="/evidence">{t('worklist.go_evidence')}</Link>
                        <br />
                      </>
                    )}
                    <Link to={`/batches/${batch}`}>{t('worklist.go_batch')}</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </main>
  );
}
